// eslint-disable-next-line no-restricted-imports
import posthog from "posthog-js";
// Side-effect import — registers __PosthogExtensions__.initSessionRecording
// before posthog.init() runs, so posthog-js takes the synchronous branch in
// startRecording and never lazy-loads lazy-recorder.js via <script> tag.
// The script-tag path is what produces the "Called on script loaded before
// session recording is available" error when onload fires without the body
// having registered the extension (ad-blockers, stale CDN cache, MITM
// proxies returning empty 200s). Pairs with the module.full alias in
// vite.config.mts, which provides __PosthogExtensions__.rrweb.record.
import "posthog-js/dist/lazy-recorder";
import {
  CAMPAIGN_PARAMS,
  refusesAnalytics,
} from "../../server/visitTracking.mjs";
import { isMarketingDocument } from "@/routing/marketingDocument";
import { gaClientId, recordGaIds, trackingId } from "@/tracking/trackingIds";

/**
 * The PostHog client every page shares, marketing and dashboard alike. The
 * client entry starts it before hydration, `PHProvider` hands it to React,
 * and `usePageViews` counts the pages.
 *
 * A signed-out visitor stays anonymous. Signing in identifies them by their
 * account's tracking id (`useSignedInUser`), and a sign-up page by the one
 * the new account will take (`useSignUpIdentity`). PostHog merges what they
 * did before into that account, on www and the dashboard alike: it merges
 * only a visitor who is still anonymous.
 */

/** This page's campaign, null where it has none, so an earlier visit's does not linger. */
function campaignParams() {
  const params = new URLSearchParams(location.search);
  return Object.fromEntries(
    CAMPAIGN_PARAMS.map((key) => [key, params.get(key)])
  );
}

/**
 * Whether the visitor turned analytics off on the CookieYes banner, which only
 * marketing pages show.
 */
function analyticsRefused(): boolean {
  if (!isMarketingDocument()) return false;
  return refusesAnalytics(
    /(?:^|;\s*)cookieyes-consent=([^;]*)/.exec(document.cookie)?.[1]
  );
}

/**
 * Applies a choice made on the banner from then on, on this page as well.
 * CookieYes writes its cookie before it tells the page, and it also tells the
 * page on load, with whatever the cookie holds.
 */
function followConsent() {
  if (analyticsRefused()) {
    if (posthog.__loaded) posthog.opt_out_capturing();
  } else if (!posthog.__loaded) {
    start();
  } else if (posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing();
  }
}

/** Google Analytics' ids, for the backend and as `$device_id`, once Analytics has set them. */
function recordGa(id: string): boolean {
  recordGaIds();
  const gaId = gaClientId();
  if (gaId) posthog.register({ $device_id: `${id}-${gaId}` });
  return Boolean(gaId);
}

/**
 * Analytics writes its cookies once GTM has loaded it and Google's consent
 * wait (`wait_for_update`) is over, after the client starts on a first visit
 * and at no moment the page can listen for, so they are looked for a while.
 */
function recordGaOnceSet(id: string, tries = 20) {
  if (recordGa(id) || tries === 0) return;
  setTimeout(() => recordGaOnceSet(id, tries - 1), 500);
}

function start() {
  const marketingPage = isMarketingDocument();
  posthog.init(process.env.NEXT_PUBLIC_POST_HOG_API_KEY as string, {
    // PostHog managed reverse proxy (bypasses ad blockers). PostHog runs it
    // and routes both ingestion and /static/* assets, so no local rewrite is
    // needed. See r-1744 / r-1747.
    api_host: "https://sakotis.webshare.io",
    api_transport: "fetch", // https://github.com/PostHog/posthog-js/issues/1487
    ui_host: "https://us.posthog.com",
    persistence: "localStorage+cookie",
    autocapture: true,
    // `usePageViews` counts the pages, and `loaded` registers the campaign.
    capture_pageview: false,
    store_google: false,
    disable_web_experiments: false,
    // Marketing pages record as GTM's snippet did: inputs masked, and console
    // logs as the project's settings say. The dashboard records them as before.
    enable_recording_console_log: marketingPage ? undefined : true,
    session_recording: {
      maskAllInputs: marketingPage,
    },
    // A refusal also clears PostHog's cookie and storage.
    opt_out_persistence_by_default: true,
    // On a page in a www experiment the server picked the visitor's id and
    // arms before drawing it (`server/experimentPages.mjs`), so PostHog starts
    // as that visitor, in those arms.
    ...(window.__PH_BOOTSTRAP__ ? { bootstrap: window.__PH_BOOTSTRAP__ } : {}),
    loaded(client) {
      // Ahead of every event, since PostHog takes a session's source and
      // channel from its first: on a first visit to www that is the `$set`
      // of `createPersonProfile`, sent before the page view.
      client.register(campaignParams());

      // GTM's tags reach the client through `window.posthog`.
      window.posthog = client;
      // Only until GTM's PostHog tag is deleted: its snippet takes a client
      // without the snippet's `__SV` mark for its own pre-load stub, and
      // boots a second client.
      (client as unknown as { __SV: number }).__SV = 1;

      // It only starts where analytics is allowed, so an opt-out it remembers
      // from a refusal the visitor has since taken back no longer stands.
      if (client.has_opted_out_capturing()) {
        client.opt_in_capturing();
      }

      // Made on a first visit: the backend gives it to the account the
      // visitor creates, and `$device_id` carries it.
      const id = trackingId();
      // Marketing pages give the anonymous visitor a person profile, which
      // cohorts and person properties need to target www's experiments.
      if (marketingPage) client.createPersonProfile();
      recordGaOnceSet(id);
      // `_sid` lasts 30 minutes, as Analytics' session does after its last
      // hit, so it's written again with each event the visitor sends. A long
      // visit to the dashboard, which changes pages without loading one, still
      // has it at checkout. Recordings send too often to be worth it.
      client.on("eventCaptured", (event: { event?: string }) => {
        if (event?.event !== "$snapshot") recordGaIds();
      });
    },
  });
}

export function startPostHog(): void {
  try {
    if (isMarketingDocument()) {
      document.addEventListener("cookieyes_consent_update", followConsent);
    }
    if (!analyticsRefused()) start();
  } catch {
    // Tracking must never stop the page from booting.
  }
}
